import { HalloweenArt } from "../../../../theme/HalloweenArt";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { State } from "../../../../reducer";
import { postOrder } from "../../../../api/order.api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

type ModalProps = { open: boolean; onClose: () => void };

const TYPE_LABEL: Record<string, { label: string; emoji: string }> = {
  hot:    { label: "Hot",    emoji: "🔥" },
  cold:   { label: "Cold",   emoji: "🧊" },
  frappe: { label: "Frappe", emoji: "🥤" },
  "extra-shot":  { label: "Extra shot", emoji: "⚡" },
  "reduce-shot": { label: "Reduce shot", emoji: "🪶" },
};

export const OrderSummary: FC<ModalProps> = ({ open, onClose }) => {
  const user = useSelector((state: State) => state.user);
  const order = useSelector((state: State) => state.order);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setImageLoading] = useState(false);
  const [isInRadius, setIsInRadius] = useState(false);
  const [isLocationAllow, setIsLocationAllow] = useState(false);

  // Location check disabled — assume in radius (preserves prior dev behavior)
  useEffect(() => {
    setIsInRadius(true);
    setIsLocationAllow(true);
  }, []);

  useEffect(() => {
    if (!open || !order.tumblerImage) return;
    setImageLoading(true);
    const img = new Image();
    img.src = order.tumblerImage;
    img.onload = () => setImageLoading(false);
    img.onerror = () => setImageLoading(false);
  }, [open, order.tumblerImage]);

  if (!open) return null;

  const isAdmin = user?.role === "admin" || user?.role === "dev";
  const typeMeta = TYPE_LABEL[order.type] ?? { label: order.type, emoji: "" };

  const notify = (message: string, kind: "success" | "warn" | "error" = "success") => {
    const colors = {
      success: { bg: "var(--accent)", text: "var(--accent-on)" },
      warn:    { bg: "#FBBF24", text: "#1F1F1F" },
      error:   { bg: "#DC2626", text: "#fff" },
    }[kind];
    toast(
      <div className="font-sans flex items-start gap-3 px-1 py-1">
        <div
          className="flex-shrink-0 w-[24px] h-[24px] rounded-full flex items-center justify-center"
          style={{ background: colors.bg, color: colors.text }}
        >
          {kind === "success" ? (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
          ) : (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01"/>
            </svg>
          )}
        </div>
        <div className="text-sm">
          <p className="font-semibold text-ink-1000">{message}</p>
        </div>
      </div>,
      { position: toast.POSITION.TOP_CENTER, autoClose: 3000 }
    );
  };

  const handlePlaceOrder = async () => {
    if (!isInRadius) {
      if (!isLocationAllow) alert("🌍 กรุณาเปิดการตรวจหา Location ในบราวเซอร์เพื่อใช้งาน");
      else alert("ฮั่นแน่! ถึงออฟฟิศก่อนค่อยกดสั่ง 👩🏻‍💻");
      return;
    }

    setIsLoading(true);
    localStorage.setItem("recentUpload", order.tumblerImage);

    try {
      const result = await postOrder(order);
      if (result.status === "ordered") {
        notify("Order placed successfully!");
        if (result.breakNotice) {
          toast.warn(result.breakNotice, { position: toast.POSITION.TOP_CENTER, autoClose: 5000 });
        }
        await new Promise((r) => setTimeout(r, 1500));
        navigate(isAdmin ? "/statusOrder-admin" : "/statusOrder", { replace: true });
      }
    } catch (err) {
      console.error(err);
      notify("Order failed — barista may have set this menu inactive", "error");
      await new Promise((r) => setTimeout(r, 2000));
      navigate(isAdmin ? "/menuCafe-admin" : "/menuCafe", { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="cafe-summary fixed inset-0 z-50 bg-white font-sans overflow-y-auto">
        <div className="min-h-screen flex flex-col">
          {/* Top bar */}
          <header className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-ink-100">
            <div className="px-3 h-[56px] flex items-center">
              <button
                type="button"
                onClick={onClose}
                aria-label="Back"
                className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
              <p className="font-display font-bold text-base text-ink-1000 flex-1 text-center pr-[40px]">
                Review your order
              </p>
            </div>
          </header>

          {/* Body */}
          <main className="flex-1 px-5 pt-[24px] pb-[120px] max-w-2xl mx-auto w-full">
            {/* Hero — drink name + type chip */}
            <div className="mb-[24px]">
              <HalloweenArt variant="ghost-coffee" className="art-heading" />
              <p className="eyebrow-v2 mb-2">Order summary</p>
              <h1 className="font-display font-black text-[36px] leading-[1.1] tracking-tightest text-ink-1000 break-words">
                {order.menu_name}
              </h1>
              <div className="mt-3 inline-flex items-center gap-2 px-3 h-[34px] rounded-full bg-ink-100 text-sm font-semibold text-ink-1000">
                <span>{typeMeta.emoji}</span>
                <span>{typeMeta.label}</span>
                <span className="text-ink-400">·</span>
                <span>{order.sweetness}% sweet</span>
              </div>
            </div>

            {/* Tumbler card — prominent */}
            <div className="mb-[24px]">
              <p className="eyebrow-v2 mb-2">Your tumbler</p>
              <div className="rounded-3xl bg-ink-50 p-4 flex items-center justify-center min-h-[260px]">
                {isImageLoading ? (
                  <div className="flex flex-col items-center gap-2">
                    <svg className="w-6 h-6 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
                      <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    </svg>
                    <span className="text-xs text-ink-500">Loading…</span>
                  </div>
                ) : order.tumblerImage ? (
                  <img
                    src={order.tumblerImage}
                    alt="Tumbler"
                    className="w-full max-w-[220px] aspect-square object-cover rounded-2xl"
                  />
                ) : (
                  <p className="text-sm text-ink-500">No image</p>
                )}
              </div>
            </div>

            {/* Notes */}
            {order.remark && order.remark !== "-" && (
              <div className="mb-[24px]">
                <p className="eyebrow-v2 mb-2">Notes</p>
                <div className="rounded-2xl bg-ink-50 px-[16px] py-3 text-sm text-ink-900 break-words">
                  {order.remark}
                </div>
              </div>
            )}

            {/* Specs grid */}
            <div className="grid grid-cols-2 gap-3 mb-[16px]">
              <Spec label="Temperature" value={`${typeMeta.emoji} ${typeMeta.label}`} />
              <Spec label="Sweetness" value={`${order.sweetness}%`} />
            </div>

            {/* Reminder */}
            <div className="rounded-2xl bg-amber-50 border border-amber-200 px-[16px] py-3 flex items-start gap-2.5">
              <svg className="w-5 h-5 flex-shrink-0 text-amber-700 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0l-7.1 12.25A2 2 0 005 19z"/>
              </svg>
              <p className="text-xs text-amber-900 leading-relaxed">
                <span className="font-semibold">Drop your tumbler at the bar.</span>{" "}
                Orders are cancelled if the tumbler isn't placed within 5 minutes.
              </p>
            </div>
          </main>

          {/* Sticky CTA */}
          <div className="fixed bottom-0 left-0 right-0 md:left-[280px] z-20 bg-white/95 backdrop-blur-md border-t border-ink-100 px-5 py-3">
            <div className="max-w-2xl mx-auto flex gap-2.5">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="btn-v2-secondary btn-v2-lg flex-1"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handlePlaceOrder}
                disabled={isLoading || isImageLoading}
                className="btn-v2-primary btn-v2-lg flex-[2]"
              >
                {isLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
                      <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    </svg>
                    Placing order
                  </>
                ) : (
                  <>
                    Place order
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

const Spec: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-2xl border border-ink-100 px-[16px] py-3">
    <p className="text-[11px] font-bold tracking-[0.08em] uppercase text-ink-500 mb-0.5">{label}</p>
    <p className="text-base font-display font-bold text-ink-1000 truncate">{value}</p>
  </div>
);
